import { subYears, startOfYear } from "date-fns";
import { SUBMIT_DEcomponents } from "fiskaly";
import { GQFiskalyEstablishment, GQFiskalyTaxPayerType } from "graphql-sdk";
import {
    FiskalySettingsInput,
    ICashRegister,
    IIntegration,
    today,
    ValidationSchema,
} from "lib";

export const schemaFiskalySettings: ValidationSchema<FiskalySettingsInput> = {
    organizationId: { required: true },
    tssId: { required: true },
};

export type FiskalyEstablishmentSubmissionForm = {
    establishmentId: GQFiskalyEstablishment["id"];
};

export const defaultEstablishmentSubmissionForm: FiskalyEstablishmentSubmissionForm =
    {
        establishmentId: null,
    };

export const schemaEstablishmentSubmissionForm: ValidationSchema<FiskalyEstablishmentSubmissionForm> =
    {
        establishmentId: { required: true },
    };

type GermanTaxInformation = {
    taxNumber: string;
    taxOfficeNumber: string;
    vatNumber: string;
};

type GermanAddressInformation = {
    street: string;
    houseNumber: string;
    houseNumberSuffix: string;
    zip: string;
    town: string;
    country: string;
};

type WebAddress = {
    webAddress: string;
};

export type FiskalyTaxPayerLegalFormType = {
    type: GQFiskalyTaxPayerType;

    legalForm: SUBMIT_DEcomponents["schemas"]["LegalForm"];
    companyName: string;
} & GermanTaxInformation &
    GermanAddressInformation &
    WebAddress;

export type FiskalyTaxPayerNaturalFormType = {
    type: GQFiskalyTaxPayerType;

    salutation: string;
    firstName: string;
    lastName: string;
    title: string;
    prefix: string;
    suffix: string;
    identificationNumber: string;

    birthDate: Date;
} & GermanTaxInformation &
    GermanAddressInformation &
    WebAddress;

export const defaultTaxPayerLegalForm: FiskalyTaxPayerLegalFormType = {
    type: GQFiskalyTaxPayerType.Legal,

    companyName: "",
    legalForm: "" as SUBMIT_DEcomponents["schemas"]["LegalForm"],
    webAddress: "",

    street: "",
    houseNumber: "",
    houseNumberSuffix: "",
    zip: "",
    town: "",
    country: "DE",

    taxNumber: "",
    taxOfficeNumber: "",
    vatNumber: "",
};

export const defaultTaxPayerNaturalForm: FiskalyTaxPayerNaturalFormType = {
    type: GQFiskalyTaxPayerType.Legal,

    salutation: "1", // Default: 1 = Herr, 2 = Frau, 3 = Divers
    firstName: "",
    lastName: "",
    title: "",
    prefix: "",
    suffix: "",
    identificationNumber: "",
    birthDate: subYears(startOfYear(today()), 15), // Best guess: A person registering a business is not born today. Let's start a while back...

    webAddress: "",

    street: "",
    houseNumber: "",
    houseNumberSuffix: "",
    zip: "",
    town: "",
    country: "DE",

    taxNumber: "",
    taxOfficeNumber: "",
    vatNumber: "",
};

const schemaGermanAddressInformation: ValidationSchema<GermanAddressInformation> =
    {
        street: { required: true },
        houseNumber: {
            required: true,
            integer: true,
            maxLength: { length: 4 },
        },
        houseNumberSuffix: {
            maxLength: { length: 20 },
        },
        zip: {
            required: true,
            integer: true,
            minLength: { length: 5 },
            maxLength: { length: 5 },
        },
        town: {
            required: true,
            maxLength: { length: 72 },
        },
        country: { required: true },
    };

const schemaGermanTaxInformation: ValidationSchema<GermanTaxInformation> = {
    taxNumber: { required: true },
    taxOfficeNumber: {
        maxLength: { length: 4 },
    },
    vatNumber: {
        maxLength: {
            length: 11,
        },
    },
};

const schemaWebAddress: ValidationSchema<WebAddress> = {
    webAddress: {
        maxLength: { length: 1000 },
    },
};

export const schemaTaxPayerLegalForm: ValidationSchema<FiskalyTaxPayerLegalFormType> =
    {
        ...schemaGermanAddressInformation,
        ...schemaGermanTaxInformation,
        ...schemaWebAddress,

        companyName: { required: true, maxLength: { length: 120 } },
        legalForm: { required: true },
    };

export const schemaTaxPayerNaturalForm: ValidationSchema<FiskalyTaxPayerNaturalFormType> =
    {
        ...schemaGermanAddressInformation,
        ...schemaGermanTaxInformation,
        ...schemaWebAddress,

        salutation: {
            required: true,
        },
        title: {
            maxLength: { length: 30 },
        },
        prefix: {
            maxLength: { length: 30 },
        },
        firstName: {
            required: true,
            maxLength: { length: 72 },
        },
        lastName: {
            required: true,
            maxLength: { length: 72 },
        },
        suffix: {
            maxLength: { length: 25 },
        },

        identificationNumber: {
            required: true,
            integer: true,
            maxLength: { length: 11 },
            minLength: { length: 11 },
        },
        birthDate: { required: true },
    };

export type FiskalyEstablishment = {
    id: string;
    integrationId: IIntegration["id"];
    organizationId: string;
    designation: string;
    street: string;
    town: string;
    zip: string;
    houseNumber: string;
    houseNumberSuffix: string | null;
    addressAdditional: string | null;
    remarks: string;

    createdAt: Date;
    updatedAt: Date | null;
    submitBefore: Date | null;
    submittedAt: Date | null;
};

export type FiskalyEstablishmentInput = Omit<
    FiskalyEstablishment,
    | "id"
    | "integrationId"
    | "organizationId"
    | "createdAt"
    | "updatedAt"
    | "submitBefore"
    | "submittedAt"
> & {
    id?: string;
};

export const schemaFiskalyEstablishmentInput: ValidationSchema<FiskalyEstablishmentInput> =
    {
        designation: { required: true },
        houseNumber: { required: true },
        street: { required: true },
        town: { required: true },
        zip: {
            required: true,
            minLength: { length: 5 },
            maxLength: { length: 5 },
        },
    };

export type FiskalyEstablishmentMappingType = {
    establishmentMap: {
        clientId: string;
        cashRegisterId: ICashRegister["id"];
        establishmentId: FiskalyEstablishment["id"];
    }[];
};
